import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Tooltip,
  Slider,
  RadioGroup,
  FormControl,
  FormLabel,
  Radio,
  FormControlLabel,
} from '@mui/material';
import { useHelpContent } from '../../utils/HelpUtils';

export type SuggestTreeImportMode = 'category_tree' | 'content_only';

interface SuggestTreeContextDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: (
    context: string,
    maxDepth: number,
    treeStrategy: 'top_down' | 'bottom_up',
    importMode: SuggestTreeImportMode,
    charsPerSegment: number,
  ) => void;
  fileNames: string[];
}

const DEFAULT_CHARS_PER_SEGMENT = 600;
const MIN_CHARS_PER_SEGMENT = 100;
const MAX_CHARS_PER_SEGMENT = 20000;

const SuggestTreeContextDialog: React.FC<SuggestTreeContextDialogProps> = ({
  open,
  onClose,
  onProceed,
  fileNames,
}) => {
  const [context, setContext] = useState('');
  const [maxDepth, setMaxDepth] = useState(1);
  const [treeStrategy, setTreeStrategy] = useState<'top_down' | 'bottom_up'>('bottom_up');
  const [importMode, setImportMode] = useState<SuggestTreeImportMode>('category_tree');
  const [charsPerSegment, setCharsPerSegment] = useState<number>(DEFAULT_CHARS_PER_SEGMENT);
  const { getTooltipTextForState } = useHelpContent();

  const clampChars = (value: number) =>
    Math.max(MIN_CHARS_PER_SEGMENT, Math.min(MAX_CHARS_PER_SEGMENT, value));

  const handleProceed = () => {
    onProceed(context.trim(), maxDepth, treeStrategy, importMode, clampChars(charsPerSegment));
  };

  const handleClose = () => {
    setContext('');
    setMaxDepth(1);
    setTreeStrategy('bottom_up');
    setImportMode('category_tree');
    setCharsPerSegment(DEFAULT_CHARS_PER_SEGMENT);
    onClose();
  };

  const isProceedDisabled = () => {
    return false; // Allow proceeding even without context
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>New document from files</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Selected files
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {fileNames.map((fileName, index) => (
              <Chip key={index} label={fileName} variant="outlined" size="small" />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">
              <Tooltip title={getTooltipTextForState('suggest-tree-import-mode', true)} placement="top">
                <span>Import mode</span>
              </Tooltip>
            </FormLabel>
            <RadioGroup
              value={importMode}
              onChange={(e) => setImportMode(e.target.value as SuggestTreeImportMode)}
            >
              <FormControlLabel
                value="category_tree"
                control={<Radio />}
                label="Build category structure"
              />
              <FormControlLabel
                value="content_only"
                control={<Radio />}
                label="Content only (no folders)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Tooltip title={getTooltipTextForState('suggest-tree-chars-per-section', true)} placement="top">
            <TextField
              type="number"
              label="Characters per section"
              value={charsPerSegment}
              onChange={(e) => {
                const parsed = parseInt(e.target.value, 10);
                setCharsPerSegment(Number.isNaN(parsed) ? DEFAULT_CHARS_PER_SEGMENT : parsed);
              }}
              onBlur={() => setCharsPerSegment((prev) => clampChars(prev))}
              inputProps={{ min: MIN_CHARS_PER_SEGMENT, max: MAX_CHARS_PER_SEGMENT, step: 100 }}
              variant="outlined"
              size="small"
              helperText={`Target size before starting a new section (default ${DEFAULT_CHARS_PER_SEGMENT})`}
              sx={{ width: 260 }}
            />
          </Tooltip>
        </Box>

        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Context (optional)"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="e.g. legal contracts; organize by chapter"
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </Tooltip>

        {importMode === 'category_tree' && (
          <Box sx={{ mb: 2 }}>
            <FormControl component="fieldset" variant="standard">
              <FormLabel component="legend">
                <Tooltip title={getTooltipTextForState('suggest-tree-strategy', true)} placement="top">
                  <span>Building strategy</span>
                </Tooltip>
              </FormLabel>
              <RadioGroup
                row
                value={treeStrategy}
                onChange={(e) => setTreeStrategy(e.target.value as 'top_down' | 'bottom_up')}
              >
                <FormControlLabel
                  value="top_down"
                  control={<Radio />}
                  label="Top-down (broad first)"
                />
                <FormControlLabel
                  value="bottom_up"
                  control={<Radio />}
                  label="Bottom-up (fine then group, default)"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {importMode === 'category_tree' && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Maximum tree depth: {maxDepth}
            </Typography>
            <Tooltip title={getTooltipTextForState('suggest-tree-max-depth', true)} placement="top">
              <Slider
                value={maxDepth}
                onChange={(_, value) => setMaxDepth(value as number)}
                min={1}
                max={6}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Tooltip>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={handleClose}>Cancel</Button>
        </Tooltip>
        <Tooltip title={getTooltipTextForState('suggest-tree-proceed', true)} placement="top">
          <Button 
            onClick={handleProceed} 
            variant="contained" 
            disabled={isProceedDisabled()}
          >
            {importMode === 'content_only' ? 'Prepare content' : 'Generate preview'}
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default SuggestTreeContextDialog; 